/* Visual behavior only. Product forms and cart updates remain in Dawn's commerce components. */
(() => {
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          target.classList.add('lm-in');
          observer.unobserve(target);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 })
    : null;

  function imageFallback(image) {
    const box = image.closest('.lm-card__media, .lm-coll__media, .lm-charm, .lm-scene__arch');
    if (!box || box.classList.contains('lm-media-fallback')) return;
    // A missing hover photograph must not hide a valid primary product photograph.
    if (box.classList.contains('lm-card__media') && image !== box.querySelector('img')) {
      image.remove();
      return;
    }
    box.classList.add('lm-media-fallback');
    const template = document.getElementById('lm-image-fallback');
    if (template) box.append(template.content.cloneNode(true));
  }

  function randomizeHeroCharms(scene) {
    const products = scene.querySelector('[data-lm-charm-products]')?.content.querySelectorAll('a');
    const charms = scene.querySelectorAll('[data-lm-product-charm]');
    if (!products?.length || charms.length !== 2) return;

    const first = Math.floor(Math.random() * products.length);
    const last = (first + 1 + Math.floor(Math.random() * (products.length - 1))) % products.length;
    [first, last].forEach((index, position) => {
      const product = products[index];
      const charm = charms[position];
      charm.href = product.getAttribute('href');
      charm.setAttribute('aria-label', product.getAttribute('aria-label'));
      charm.replaceChildren(product.querySelector('img').cloneNode(true));
    });
  }

  function initialize(root) {
    root.querySelectorAll('[data-lm-rise], .lm-coll, .lm-stitch').forEach((target) => {
      if (observer && !reduceMotion.matches) observer.observe(target);
      else target.classList.add('lm-in');
    });
    root.querySelectorAll('[data-lm-scene]').forEach((scene) => {
      if (initialized.has(scene)) return;
      initialized.add(scene);
      randomizeHeroCharms(scene);
      const mobile = scene.querySelector('[data-lm-mobile]');
      let frame;
      scene.addEventListener('pointermove', (event) => {
        if (!mobile || reduceMotion.matches || !matchMedia('(pointer: fine)').matches) return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const bounds = scene.getBoundingClientRect();
          mobile.style.setProperty('--tilt', (((event.clientX - bounds.left) / bounds.width - 0.5) * 12).toFixed(2));
        });
      });
      scene.addEventListener('pointerleave', () => {
        cancelAnimationFrame(frame);
        mobile?.style.setProperty('--tilt', 0);
      });
    });
    root.querySelectorAll('.lm-section img').forEach((image) => {
      if (initialized.has(image)) return;
      initialized.add(image);
      image.addEventListener('error', () => imageFallback(image), { once: true });
      if (image.complete && image.naturalWidth === 0) imageFallback(image);
    });
  }

  initialize(document);
  document.documentElement.classList.add('lm-motion');
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-lm-rise], .lm-coll, .lm-stitch').forEach((target) => observer?.unobserve(target));
  });
  document.addEventListener('shopify:block:select', (event) => {
    event.target.classList.add('lm-in');
    event.target.closest('[data-lm-rise]')?.classList.add('lm-in');
  });

  if (!CSS.supports('animation-timeline', 'scroll()')) {
    const progress = document.querySelector('.lm-progress');
    const updateProgress = () => {
      const distance = document.documentElement.scrollHeight - innerHeight;
      if (progress) progress.style.transform = `scaleX(${distance > 0 ? scrollY / distance : 0})`;
    };
    updateProgress();
    addEventListener('scroll', updateProgress, { passive: true });
    addEventListener('resize', updateProgress);
  }

  let feedbackTimer;
  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-lm-cookie-preferences]');
    if (!link || !window.privacyBanner?.showPreferences) return;
    event.preventDefault();
    try {
      await window.privacyBanner.showPreferences();
    } catch {
      const feedback = document.querySelector('[data-lm-feedback]');
      if (!feedback) return;
      feedback.textContent = link.dataset.errorMessage;
      feedback.classList.add('lm-show');
      clearTimeout(feedbackTimer);
      feedbackTimer = setTimeout(() => feedback.classList.remove('lm-show'), 6000);
    }
  });
})();

class LilimooHeader extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.sheet = this.querySelector('.lm-sheet');
    this.burger = this.querySelector('.lm-burger');
    const header = this.querySelector('.lm-hdr');
    const search = this.querySelector('.lm-srch');
    const searchButton = search.querySelector('.lm-search-submit');
    const searchInput = search.querySelector('[name="q"]');
    const setSearchOpen = (open) => {
      search.classList.toggle('lm-open', open);
      searchButton.setAttribute('aria-expanded', String(open));
    };
    this.burger.addEventListener('click', () => this.setOpen(true), { signal });
    this.querySelector('.lm-sheet__close').addEventListener('click', () => this.setOpen(false), { signal });
    this.sheet.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.setOpen(false);
    }, { signal });
    search.addEventListener('submit', (event) => {
      if (search.classList.contains('lm-open') && searchInput.value.trim()) return;
      event.preventDefault();
      setSearchOpen(true);
      searchInput.focus({ preventScroll: true });
    }, { signal });
    search.addEventListener('focusout', (event) => {
      if (!search.contains(event.relatedTarget)) setSearchOpen(false);
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!search.contains(event.target)) setSearchOpen(false);
      this.querySelectorAll('.lm-nav details[open]').forEach((details) => {
        if (!details.contains(event.target)) details.open = false;
      });
    }, { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (this.sheet.classList.contains('lm-open')) this.setOpen(false);
      if (search.contains(document.activeElement)) {
        setSearchOpen(false);
        searchButton.focus();
      }
      this.querySelectorAll('.lm-nav details[open]').forEach((details) => {
        details.open = false;
        details.querySelector('summary').focus();
      });
    }, { signal });
    const onScroll = () => header.classList.toggle('lm-is-stuck', scrollY > 12);
    onScroll();
    addEventListener('scroll', onScroll, { passive: true, signal });
    addEventListener('resize', () => {
      if (innerWidth > 880 && this.sheet.classList.contains('lm-open')) this.setOpen(false);
    }, { signal });
  }

  setOpen(open) {
    this.sheet.classList.toggle('lm-open', open);
    this.sheet.setAttribute('aria-hidden', String(!open));
    this.sheet.inert = !open;
    this.burger.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('lm-menu-open', open);
    if (open) trapFocus(this.sheet, this.sheet.querySelector('.lm-sheet__close'));
    else removeTrapFocus(this.burger);
  }

  disconnectedCallback() {
    if (this.sheet?.classList.contains('lm-open')) this.setOpen(false);
    this.controller?.abort();
  }
}

if (!customElements.get('lilimoo-header')) customElements.define('lilimoo-header', LilimooHeader);
